/**
 * Lends the Requests workspace to Casey's floating panel while the client
 * profile is open: the open intake or submission rides every turn as the
 * selection, `data-open-artifact` opens the page Casey names, `data-invalidate`
 * refetches the list and pages, and Ask Casey prompts and row mentions reach
 * the composer.
 */
import { useCallback, useEffect, useMemo, useRef, type FC } from "react";

import type {
  InvalidateTarget,
  OpenArtifactData
} from "@/features/caseIde/types";
import {
  useAgentChatHostStore,
  type AgentChatHost,
  type HostPromptListener
} from "@/features/agents/agentChatHostStore";
import type { SerializedSelection } from "@/features/agents/useAgentChat";

import { clearBuilding, markBuilding } from "../buildingDrafts";
import { useRequestsIde } from "../context/RequestsIdeContext";
import { useRequestsData } from "../data/RequestsDataContext";
import { parseTemplateArtifactId } from "../model";
import type { RequestArtifact, RequestsInvalidateTarget } from "../types";
import { parseSubmissionId } from "../types";

export const RequestsChatHost: FC = () => {
  const ide = useRequestsIde();
  const data = useRequestsData();
  const { clientId } = ide;

  // The adapters read the latest IDE state through refs so the host, and
  // the chat's transport, do not rebuild on every selection change.
  const ideRef = useRef(ide);
  ideRef.current = ide;
  const dataRef = useRef(data);
  dataRef.current = data;

  const selection = useCallback((): SerializedSelection | null => {
    const { selection: sel } = ideRef.current;
    const { artifact } = sel;
    const out: SerializedSelection = {
      artifact: null,
      text: null,
      focus: { kind: "client", id: clientId }
    };
    if (!artifact) return out;
    if (artifact.kind === "intake" || artifact.kind === "preview") {
      const intake = dataRef.current.intakes.find(
        (i) => i.outgoing_intake_id === artifact.id
      );
      out.artifact = {
        kind: "intake",
        id: artifact.id,
        title: intake?.title ?? artifact.title
      };
      out.open_intake = {
        id: artifact.id,
        title: intake?.title ?? artifact.title,
        status: intake?.status ?? null,
        selected_node_ids: sel.selectedNodeIds
      };
    } else if (artifact.kind === "submission") {
      const ids = parseSubmissionId(artifact.id);
      if (ids) {
        const intake = dataRef.current.intakes.find(
          (i) => i.outgoing_intake_id === ids.intakeId
        );
        out.artifact = {
          kind: "submission",
          id: artifact.id,
          title: artifact.title
        };
        out.open_intake = {
          id: ids.intakeId,
          title: intake?.title ?? null,
          status: intake?.status ?? null
        };
        out.open_submission = {
          intake_id: ids.intakeId,
          submission_id: ids.submissionId,
          selected_answer_key: sel.selectedAnswerKey
        };
      }
    } else if (artifact.kind === "template") {
      out.artifact = {
        kind: "template",
        id: artifact.id,
        title: artifact.title
      };
      out.open = {
        kind: "template",
        id: artifact.id,
        label: artifact.title ?? null
      };
    }
    if (out.open_intake)
      out.focus = {
        kind: "client",
        id: clientId,
        intake_id: out.open_intake.id
      };
    return out;
  }, [clientId]);

  const onOpenArtifact = useCallback((a: OpenArtifactData) => {
    const kind = String(a.kind);
    if (kind === "intake") {
      const intake = dataRef.current.intakes.find(
        (i) => i.outgoing_intake_id === a.id
      );
      const artifact: RequestArtifact = {
        kind: "intake",
        id: a.id,
        title: a.title ?? intake?.title ?? "Intake"
      };
      markBuilding(a.id, a.building === true);
      ideRef.current.open(artifact);
      // The list may not have it yet (a draft the agent just built): refetch, then it is titled.
      if (!intake) void dataRef.current.refetch(["intakes"]);
    } else if (kind === "submission") {
      const ids = parseSubmissionId(a.id);
      if (!ids) return;
      const intake = dataRef.current.intakes.find(
        (i) => i.outgoing_intake_id === ids.intakeId
      );
      ideRef.current.open({
        kind: "submission",
        id: a.id,
        title: a.title ?? `Submission · ${intake?.title ?? "Intake"}`
      });
      void dataRef.current.loadDetail(ids.intakeId, { force: true });
    } else if (kind === "template") {
      const { saved, templateId } = parseTemplateArtifactId(a.id);
      const known = saved
        ? dataRef.current.savedTemplateById(templateId)?.name
        : dataRef.current.templateById(templateId)?.label;
      ideRef.current.open({
        kind: "template",
        id: a.id,
        title: a.title ?? known ?? "Template"
      });
      // A template the agent just saved is not in the list yet.
      if (!known) void dataRef.current.refetch(["templates"]);
    }
  }, []);

  const onInvalidate = useCallback((targets: InvalidateTarget[]) => {
    ideRef.current.invalidate(targets as RequestsInvalidateTarget[]);
  }, []);

  // Whole prompts from pages and dialogs, and row mentions: without a case
  // filesystem to mention into, the intake is named in prose, which is what
  // the agent's refs resolve on.
  const onPromptRequest = useCallback((listener: HostPromptListener) => {
    const offPrompt = ideRef.current.onPromptRequest(listener);
    const offMention = ideRef.current.onMentionRequest((artifact) => {
      const label =
        artifact.kind === "template"
          ? `the template "${artifact.title}"`
          : `the intake "${artifact.title}"`;
      listener(`About ${label}: `, { send: false });
    });
    return () => {
      offPrompt();
      offMention();
    };
  }, []);

  const host = useMemo<AgentChatHost>(
    () => ({
      scope: { kind: "client", id: clientId },
      adapters: { selection, onOpenArtifact, onInvalidate },
      onPromptRequest,
      onTurnIdle: clearBuilding
    }),
    [clientId, selection, onOpenArtifact, onInvalidate, onPromptRequest]
  );

  const register = useAgentChatHostStore((s) => s.register);
  useEffect(() => register(host), [register, host]);

  return null;
};
